import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import PageSEO from "@/components/seo/PageSEO";
import { fetchProjects } from "@/lib/api";

function formatValueGBP(v) {
    if (!v || typeof v !== "number") return null;
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
    return `£${v}`;
}

export default function ProjectsPage() {
    const [projects, setProjects] = useState([]);
    const [filter, setFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchProjects()
            .then(setProjects)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    // Filter chips are derived from the data so they stay in sync with seed edits.
    const filters = useMemo(() => {
        const cats = Array.from(new Set(projects.map((p) => p.category).filter(Boolean)));
        cats.sort();
        return ["All", ...cats];
    }, [projects]);

    const filtered = useMemo(() => {
        if (filter === "All") return projects;
        return projects.filter((p) => p.category === filter);
    }, [projects, filter]);

    return (
        <div className="bg-brand-cream" data-testid="projects-page">
            <PageSEO
                title="Recent Construction Projects — EB Construction Manchester"
                description="Selected recent works by EB Construction across residential, commercial, industrial, hospitality, education and civil sectors in Manchester and the North West."
                canonicalPath="/projects"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    url: "https://elevatebuildconstruction.co.uk/projects",
                    name: "EB Construction Projects",
                    hasPart: projects.map((p) => ({
                        "@type": "CreativeWork",
                        name: p.title,
                        url: `https://elevatebuildconstruction.co.uk/projects/${p.slug}`,
                        about: p.category,
                    })),
                }}
            />
            <section className="border-b border-brand-line py-24 md:py-32">
                <div className="container-x grid gap-12 md:grid-cols-12">
                    <div className="md:col-span-8">
                        <Reveal>
                            <SectionLabel index={0} label="Projects" />
                            <span className="rule-gold" aria-hidden="true" />
                            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] md:text-7xl">
                                A portfolio of{" "}
                                <span className="accent-italic text-brand-gold">
                                    consequence.
                                </span>
                            </h1>
                            <p
                                className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-steel"
                                data-testid="projects-honesty-note"
                            >
                                Selected recent works — case-study details available on request.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-4">
                        <Reveal delay={0.1}>
                            <p className="font-body text-lg leading-relaxed text-brand-steel">
                                A cross-section of recent work across residential, commercial,
                                industrial, hospitality, education and civils. Full case studies
                                available on request.
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div
                    className="container-x mt-12 flex flex-wrap gap-3"
                    data-testid="projects-filters"
                >
                    {filters.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`border px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                filter === f
                                    ? "border-brand-gold bg-brand-gold text-brand-charcoal"
                                    : "border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-offwhite"
                            }`}
                            data-testid={`filter-${f.toLowerCase()}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-x">
                    {loading && (
                        <p
                            className="font-mono-tag text-brand-steel"
                            data-testid="projects-loading"
                        >
                            LOADING PROJECTS…
                        </p>
                    )}
                    {error && (
                        <p className="font-body text-brand-charcoal" data-testid="projects-error">
                            Could not load projects. {error}
                        </p>
                    )}
                    {!loading && !error && (
                        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {filtered.map((p, i) => {
                                const priceLabel = formatValueGBP(p.value_gbp);
                                const cover = p.cover_image || p.image;
                                return (
                                    <Reveal key={p.id || p.slug} delay={i * 0.05}>
                                        <Link
                                            to={`/projects/${p.slug}`}
                                            className="group block border border-brand-line bg-brand-paper transition-colors hover:border-brand-charcoal"
                                            data-testid={`project-card-${p.slug}`}
                                        >
                                            <div className="aspect-[4/3] overflow-hidden">
                                                <img
                                                    src={cover}
                                                    alt={p.title}
                                                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                                />
                                            </div>
                                            <div className="border-t border-brand-line p-6">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-mono-tag text-brand-gold">
                                                        {p.category} · {p.year}
                                                    </p>
                                                    <ArrowUpRight className="h-4 w-4 text-brand-charcoal transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                                </div>
                                                <h3 className="mt-3 font-display text-2xl font-semibold leading-[1.15] md:text-3xl">
                                                    {p.title}
                                                </h3>
                                                <p className="mt-2 font-body text-sm text-brand-steel">
                                                    {p.location}
                                                </p>
                                                {p.summary && (
                                                    <p className="mt-4 font-body text-sm leading-relaxed text-brand-steel">
                                                        {p.summary}
                                                    </p>
                                                )}
                                                {priceLabel && (
                                                    <div className="mt-4 flex items-center gap-2 border-t border-brand-line pt-4">
                                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                                                            Contract value
                                                        </span>
                                                        <span className="font-industrial text-xl text-brand-gold">
                                                            {priceLabel}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </Link>
                                    </Reveal>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
